import React from 'react'
import { isNull } from 'lodash'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import {
  GroupMemberRole,
  Groups_groups_Groups,
  useAuth,
  useGroupsData,
  useRemoveCompanyGroup,
  CompanyMemberRole,
} from '@modmd/data'
import Icon from 'components/Icon'
import Card from 'components/Card'
import Button from 'components/Button'
import { Dialog, DialogContentText } from 'components/Dialog'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useToaster } from 'utils/useToaster'
import { DEVICE } from 'theme'

const PAGE_LENGTH = 10

interface CompanyGroupsFilter {
  nameLike?: string
}

interface ColumnLabels {
  groupName?: string | null
  numOfPeople?: string | null
  deleteGroup?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  filter?: CompanyGroupsFilter
  sorter?: DataListProps['sorter']
  page?: number
  pageLength?: number
  columnLabels?: ColumnLabels
  onPageChange?: (page: number) => void
  onRowClick: (data: Groups_groups_Groups) => void
}

const defaultColumnLabels = {
  groupName: 'Group name',
  numOfPeople: 'Number of people',
  deleteGroup: 'Delete',
}

export const CompanyGroupsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  filter,
  sorter,
  page,
  columnLabels: providedColumnLabels,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const [groupToRemove, setGroupToRemove] = React.useState<{
    id: string
    name: string
  } | null>(null)
  const {
    data: { User },
    getUserCompaniesWithRole,
  } = useAuth()
  const { hasCompaniesWithRole, companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })
  const { setToastMessage } = useToaster()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const { nameLike } = filter || {}
  const { data, loading, refetch } = useGroupsData({
    membersFilter: {
      isDeleted: false,
    },
    filter: {
      nameLike: nameLike ?? '',
      companyIds: companies.map(({ id }: { id: string }) => id),
      memberUserIds: [User.id],
      memberUserGroupRoles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
    },
    page,
    pageLength: PAGE_LENGTH,
    membersPageLength: 1,
  })
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  const [removeCompanyGroup, { loading: isRemoveCompanyGroupLoading }] = useRemoveCompanyGroup()

  const handleRemoveCompanyGroup = React.useCallback(async () => {
    try {
      await removeCompanyGroup({
        variables: {
          id: groupToRemove!.id,
        },
      })
      setToastMessage('Group successfully deleted', 'success')
      void refetch()
    } catch {
      // ignore
    }
    setGroupToRemove(null)
  }, [groupToRemove, refetch, removeCompanyGroup, setToastMessage])

  const groupCount = data?.groups?.pagination.total ?? 0
  const groupsPageLength = data?.groups?.pagination.length ?? 0

  return (
    <>
      <Card mb="1rem">
        <Datalist
          isLoading={loading}
          data={data?.groups?.Groups || []}
          sorter={sorter}
          header={tableHeader}
          footer={tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row
                    gridTemplateColumns={
                      hasCompaniesWithRole ? rowGridTemplateColumns : 'repeat(2, 1fr)'
                    }
                  >
                    {!isNull(columnLabels.groupName) ? (
                      <Datalist.HeaderCell label={columnLabels.groupName} />
                    ) : null}
                    {!isNull(columnLabels.numOfPeople) ? (
                      <Datalist.HeaderCell label={columnLabels.numOfPeople} />
                    ) : null}
                    {!isNull(columnLabels.deleteGroup) && hasCompaniesWithRole ? (
                      <Datalist.HeaderCell label={columnLabels.deleteGroup} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const { name, Members, id } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: hasCompaniesWithRole ? rowGridTemplateColumns : 'repeat(2, 1fr)',
                }}
                onClick={() => {
                  onRowClick(data)
                }}
                {...(!isTablet ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.groupName) ? (
                  <Datalist.Cell label={columnLabels.groupName}>{name}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.numOfPeople) ? (
                  <Datalist.Cell label={columnLabels.numOfPeople}>
                    {Members?.pagination.total || 0}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.deleteGroup) && hasCompaniesWithRole ? (
                  <Datalist.Cell label={columnLabels.deleteGroup}>
                    <Button
                      size="small"
                      colorVariant="danger"
                      appearance="ghost"
                      onClick={(e) => {
                        e.stopPropagation()
                        setGroupToRemove({
                          id,
                          name,
                        })
                      }}
                    >
                      <Icon.Trash />
                    </Button>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
        />
      </Card>
      {groupCount > groupsPageLength && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(groupCount / groupsPageLength)}
            currentPage={Math.ceil((data?.groups?.pagination.from ?? 0) / groupsPageLength) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
      <Dialog
        onDismiss={() => setGroupToRemove(null)}
        isOpen={!!groupToRemove}
        variant="center"
        maxWidth={isDesktop ? '550px' : '100%'}
      >
        <Card>
          <Card.Title>Confirm delete group</Card.Title>
          <Card.Content>
            {groupToRemove?.name && (
              <DialogContentText>{`Are you sure to delete group ${groupToRemove.name}? Group will be permanently removed from company groups.`}</DialogContentText>
            )}
          </Card.Content>
          <Card.Actions>
            <Button appearance="ghost" onClick={() => setGroupToRemove(null)}>
              Cancel
            </Button>
            <Button
              colorVariant="danger"
              onClick={handleRemoveCompanyGroup}
              isFetching={isRemoveCompanyGroupLoading}
            >
              Delete
            </Button>
          </Card.Actions>
        </Card>
      </Dialog>
    </>
  )
}
